import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import UserSelector from '../components/UserSelector';
import WebcamCapture from '../components/WebcamCapture';
import { Users, Mail, Phone, Home, Shield, UserPlus, Edit, Trash2, MapPin, Search, X, Camera, Save } from 'lucide-react';

const Moradores = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [moradores, setMoradores] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMorador, setEditingMorador] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dados'); // 'dados' ou 'reconhecimento'
  const [showWebcam, setShowWebcam] = useState(false);
  const [fotoReconhecimento, setFotoReconhecimento] = useState(null);
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    rg: '',
    data_nascimento: new Date().toISOString().split('T')[0], // Data atual como padrão
    sexo: 'nao_informado', // Não informado como padrão
    estado_civil: 'solteiro', // Solteiro como padrão
    profissao: '',
    empresa: '',
    renda_mensal: '',
    observacoes: '',
    apartamento_id: '',
    condominio_id: getCondominioId(),
    tipo: 'proprietario',
    ativo: true,
    usuario_id: '', // Campo para vincular usuário
    sera_usuario: false, // Checkbox para indicar se será usuário
    senha: '' // Senha para criação de usuário
  });
  const [limiteUsuarios, setLimiteUsuarios] = useState({ quantidade: 0, podeAdicionar: true });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ nome: '', bloco: '', apartamento_id: '' });
  const [blocoCadastro, setBlocoCadastro] = useState('');
  const [filterNomeInput, setFilterNomeInput] = useState(''); // valor imediato do campo nome (evita perda de foco)

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      setFilterNomeInput('');
      loadMoradores(true);
      loadApartamentos();
      loadUsuarios();
    }
  }, [condominioSelecionado]);

  // Debounce do filtro por nome: atualiza filters.nome após 400ms sem digitar (evita perda de foco e muitas requisições)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (prev.nome === filterNomeInput ? prev : { ...prev, nome: filterNomeInput }));
      setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [filterNomeInput]);

  useEffect(() => {
    if (getCondominioId()) loadMoradores(false);
  }, [pagination.page, filters.nome, filters.bloco, filters.apartamento_id]);

  const loadMoradores = async (showFullPageLoading = false) => {
    try {
      // Só mostra spinner de tela cheia na carga inicial; ao filtrar/paginar mantém a lista visível para não perder foco do input
      if (showFullPageLoading || moradores.length === 0) setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;
      
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filters.nome?.trim()) params.append('nome', filters.nome.trim());
      if (filters.bloco?.trim()) params.append('bloco', filters.bloco.trim());
      if (filters.apartamento_id) params.append('apartamento_id', filters.apartamento_id);
      
      const response = await api.get(`/moradores?${params}`);
      const moradoresData = response.data.data.moradores || [];
      const pag = response.data.data.pagination || {};
      
      setMoradores(moradoresData);
      setPagination(prev => ({ ...prev, total: pag.total || 0, pages: pag.pages || 1 }));
    } catch (error) {
      console.error('Erro ao carregar moradores:', error);
      setMoradores([]);
    } finally {
      setLoading(false);
    }
  };

  const loadApartamentos = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) return;
      
      // O interceptor já adiciona condominio_id automaticamente
      const response = await api.get('/apartamentos', { params: { limit: 2000 } });
      setApartamentos(response.data.data.apartamentos || []);
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) return;
      
      // O interceptor já adiciona condominio_id automaticamente
      const response = await api.get('/usuarios');
      setUsuarios(response.data.data.usuarios || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      // Validar se será usuário e tem apartamento
      if (formData.sera_usuario && !formData.apartamento_id) {
        setErrorMessage('Selecione um apartamento quando o morador será usuário');
        setIsSubmitting(false);
        return;
      }

      // Validar limite de usuários se será usuário
      const jaTemUsuario = !!(editingMorador && editingMorador.usuario_id);
      const mudouUnidade = !!(editingMorador
        && String(formData.apartamento_id) !== String(editingMorador.apartamento_id));
      const criandoUsuario = formData.sera_usuario && !jaTemUsuario;
      if ((criandoUsuario || (jaTemUsuario && mudouUnidade)) && !limiteUsuarios.podeAdicionar) {
        setErrorMessage('Já existem 2 usuários cadastrados nesta unidade. É permitido no máximo 2 usuários por unidade.');
        setIsSubmitting(false);
        return;
      }

      // Preparar dados para envio
      const dadosParaEnvio = {
        ...formData,
        apartamento_id: parseInt(formData.apartamento_id),
        renda_mensal: formData.renda_mensal ? parseFloat(formData.renda_mensal) : null,
        // Converter strings vazias para null nos campos opcionais
        cpf: formData.cpf || null,
        rg: formData.rg || null,
        data_nascimento: formData.data_nascimento || null,
        sexo: formData.sexo || null,
        estado_civil: formData.estado_civil || null,
        profissao: formData.profissao || null,
        empresa: formData.empresa || null,
        observacoes: formData.observacoes || null,
        sera_usuario: formData.sera_usuario || false,
        senha: formData.sera_usuario && formData.senha ? formData.senha : undefined
      };

      if (!dadosParaEnvio.senha) {
        delete dadosParaEnvio.senha;
      }
      if (!dadosParaEnvio.usuario_id) {
        delete dadosParaEnvio.usuario_id;
      }

      if (editingMorador) {
        const moradorId = editingMorador.morador_id || editingMorador.id;
        await api.put(`/moradores/${moradorId}`, dadosParaEnvio);
      } else {
        await api.post('/moradores', dadosParaEnvio);
      }
      setShowModal(false);
      setEditingMorador(null);
      resetForm();
      loadMoradores();
    } catch (error) {
      console.error('Erro ao salvar morador:', error);
      
      // Tratar diferentes tipos de erro
      const apiErrors = error.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setErrorMessage(apiErrors.map(err => err.msg).filter(Boolean).join(', '));
      } else if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCapturarFoto = (fotoBase64) => {
    setFotoReconhecimento(fotoBase64);
    setShowWebcam(false);
    
    // Se está editando, salvar imediatamente
    if (editingMorador) {
      const moradorId = editingMorador.morador_id || editingMorador.id;
      
      if (!moradorId) {
        console.error('❌ ID do morador não encontrado!');
        alert('Erro: ID do morador não encontrado');
        return;
      }
      
      salvarFotoReconhecimento(moradorId, fotoBase64);
    }
  };

  const salvarFotoReconhecimento = async (moradorId, fotoBase64) => {
    setSalvandoFoto(true);
    try {
      console.log(`📷 Salvando foto de reconhecimento para morador ID: ${moradorId}`);
      
      const response = await api.put(`/moradores/${moradorId}/foto-reconhecimento`, {
        foto_base64: fotoBase64
      });
      
      console.log('✅ Resposta do servidor:', response.data);
      
      if (window.showNotification) {
        window.showNotification('Foto de reconhecimento salva com sucesso!', 'success');
      }
      
      // Atualizar o morador com a foto salva
      if (response.data.data?.morador) {
        const moradorAtualizado = response.data.data.morador;
        console.log('📝 Morador atualizado recebido do servidor:', {
          id: moradorAtualizado.morador_id || moradorAtualizado.id,
          tem_foto: moradorAtualizado.morador_foto_reconhecimento ? 'SIM' : 'NÃO',
          tamanho_foto: moradorAtualizado.morador_foto_reconhecimento?.length
        });
        
        setEditingMorador(moradorAtualizado);
        
        // Garantir que a foto fique visível no preview
        if (moradorAtualizado.morador_foto_reconhecimento) {
          console.log('✅ Atualizando preview da foto');
          setFotoReconhecimento(moradorAtualizado.morador_foto_reconhecimento);
        }
      }
      
      // Recarregar lista de moradores para ter dados atualizados
      setTimeout(() => {
        loadMoradores();
      }, 500);
    } catch (error) {
      console.error('❌ Erro ao salvar foto:', error);
      console.error('Resposta do servidor:', error.response?.data);
      
      const message = error.response?.data?.message || 'Erro ao salvar foto de reconhecimento';
      
      if (window.showNotification) {
        window.showNotification(message, 'error');
      } else {
        alert(message);
      }
    } finally {
      setSalvandoFoto(false);
    }
  };

  const handleEdit = (morador) => {
    setEditingMorador(morador);
    const blocoDoMorador = morador.apartamento_bloco
      || apartamentos.find((apt) => String(apt.apartamento_id) === String(morador.apartamento_id))?.apartamento_bloco
      || '';
    setBlocoCadastro(blocoDoMorador);
    setFotoReconhecimento(morador.morador_foto_reconhecimento || null);
    setActiveTab('dados'); // Começar na aba de dados
    setFormData({
      nome: morador.morador_nome || morador.nome || '',
      email: morador.morador_email || morador.email || '',
      telefone: morador.morador_telefone || morador.telefone || '',
      cpf: morador.morador_cpf || morador.cpf || '',
      rg: morador.morador_rg || morador.rg || '',
      data_nascimento: morador.morador_data_nascimento ? morador.morador_data_nascimento.split('T')[0] : (morador.data_nascimento ? morador.data_nascimento.split('T')[0] : ''),
      sexo: morador.morador_sexo || morador.sexo || '',
      estado_civil: morador.morador_estado_civil || morador.estado_civil || '',
      profissao: morador.morador_profissao || morador.profissao || '',
      empresa: morador.empresa || '',
      renda_mensal: morador.renda_mensal || '',
      observacoes: morador.observacoes || '',
      apartamento_id: morador.apartamento_id || '',
      condominio_id: getCondominioId(),
      tipo: morador.tipo || 'proprietario',
      ativo: morador.ativo !== false,
      usuario_id: morador.usuario_id || '',
      sera_usuario: !!morador.usuario_id, // Se já tem usuário vinculado, marcar como true
      senha: '' // Não preencher senha na edição
    });
    
    // Se já tem usuário vinculado, verificar limite
    if (morador.usuario_id && morador.apartamento_id) {
      verificarLimiteUsuarios(morador.apartamento_id);
    }
    
    // Carregar dados do usuário se houver
    if (morador.usuario_id) {
      const usuario = usuarios.find(u => {
        // Suportar ambos os formatos: u.usuario.usuario_id ou u.usuario_id
        const usuarioData = u.usuario || u;
        return usuarioData && usuarioData.usuario_id === morador.usuario_id;
      });
      if (usuario) {
        // Suportar ambos os formatos
        const usuarioData = usuario.usuario || usuario;
        setSelectedUser(usuarioData);
      } else {
        setSelectedUser(null);
      }
    } else {
      setSelectedUser(null);
    }
    
    console.log('✅ Abrindo modal de edição');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este morador?')) {
      try {
        await api.delete(`/moradores/${id}`);
        loadMoradores();
      } catch (error) {
        console.error('Erro ao excluir morador:', error);
      }
    }
  };

  const apartamentoDoUsuario = () => {
    const gestores = ['administrador', 'sindico', 'subsindico', 'porteiro'];
    if (gestores.includes(user?.tipo) || isAdministrador()) return '';
    const vinculo = user?.vinculos?.find((v) => String(v.condominio_id) === String(getCondominioId()));
    return vinculo?.apartamento_id || '';
  };

  const resetForm = () => {
    const hoje = new Date().toISOString().split('T')[0]; // Data atual no formato YYYY-MM-DD
    
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      cpf: '',
      rg: '',
      data_nascimento: hoje, // Data atual como padrão
      sexo: 'nao_informado', // Não informado como padrão
      estado_civil: 'solteiro', // Solteiro como padrão
      profissao: '',
      empresa: '',
      renda_mensal: '',
      observacoes: '',
      apartamento_id: apartamentoDoUsuario() || '',
      condominio_id: getCondominioId(),
      tipo: 'proprietario',
      ativo: true,
      usuario_id: '',
      sera_usuario: false,
      senha: ''
    });
    setErrorMessage('');
    setSelectedUser(null);
    setLimiteUsuarios({ quantidade: 0, podeAdicionar: true });
    setBlocoCadastro('');
  };

  // Função para verificar limite de usuários por apartamento
  const verificarLimiteUsuarios = async (apartamentoId) => {
    if (!apartamentoId) {
      setLimiteUsuarios({ quantidade: 0, podeAdicionar: true });
      return;
    }

    try {
      const response = await api.get(`/moradores/usuarios-por-apartamento/${apartamentoId}`);
      const data = response.data.data;
      setLimiteUsuarios({
        quantidade: data.quantidade_usuarios,
        podeAdicionar: data.pode_adicionar
      });
    } catch (error) {
      console.error('Erro ao verificar limite de usuários:', error);
      setLimiteUsuarios({ quantidade: 0, podeAdicionar: true });
    }
  };

  const handleUserSelect = (user) => {
    if (!user) {
      console.error('Usuário não fornecido para seleção');
      return;
    }
    
    // Suportar ambos os formatos: user.usuario_id ou user.usuario?.usuario_id
    const usuarioId = user.usuario_id || (user.usuario && user.usuario.usuario_id);
    
    if (!usuarioId) {
      console.error('ID do usuário não encontrado:', user);
      return;
    }
    
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      usuario_id: usuarioId
    }));
  };

  const handleRemoveUser = () => {
    setSelectedUser(null);
    setFormData(prev => ({
      ...prev,
      usuario_id: ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Aplicar máscara para telefone
    if (name === 'telefone') {
      const phoneValue = value.replace(/\D/g, '');
      let formattedPhone = '';
      
      if (phoneValue.length <= 2) {
        formattedPhone = phoneValue;
      } else if (phoneValue.length <= 6) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2)}`;
      } else if (phoneValue.length <= 10) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2, 6)}-${phoneValue.slice(6)}`;
      } else {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2, 7)}-${phoneValue.slice(7, 11)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
      return;
    }
    
    // Aplicar máscara para CPF
    if (name === 'cpf') {
      const cpfValue = value.replace(/\D/g, '');
      let formattedCpf = '';
      
      if (cpfValue.length <= 3) {
        formattedCpf = cpfValue;
      } else if (cpfValue.length <= 6) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3)}`;
      } else if (cpfValue.length <= 9) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6)}`;
      } else {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6, 9)}-${cpfValue.slice(9, 11)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedCpf
      }));
      return;
    }
    
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Se mudou apartamento_id e sera_usuario está marcado, verificar limite
    if (name === 'apartamento_id' && formData.sera_usuario) {
      verificarLimiteUsuarios(newValue);
    }

    // Se mudou sera_usuario, verificar limite se apartamento_id estiver preenchido
    if (name === 'sera_usuario' && checked && formData.apartamento_id) {
      verificarLimiteUsuarios(formData.apartamento_id);
    }

    // Limpar senha quando desmarcar checkbox
    if (name === 'sera_usuario' && !checked) {
      setFormData(prev => ({
        ...prev,
        senha: ''
      }));
    }
  };

  const getTipoLabel = (tipo) => {
    const tipoMap = {
      sindico: 'Síndico',
      subsindico: 'Subsíndico',
      morador: 'Morador',
      porteiro: 'Porteiro',
      administrador: 'Administrador',
      proprietario: 'Proprietário',
      inquilino: 'Inquilino',
      morador_temporario: 'Morador Temporário'
    };
    return tipoMap[tipo] || tipo;
  };

  const getSexoLabel = (sexo) => {
    const sexoMap = {
      nao_informado: 'Não informado',
      masculino: 'Masculino',
      feminino: 'Feminino',
      outro: 'Outro'
    };
    return sexoMap[sexo] || sexo;
  };

  const getEstadoCivilLabel = (estadoCivil) => {
    const estadoCivilMap = {
      solteiro: 'Solteiro(a)',
      casado: 'Casado(a)',
      divorciado: 'Divorciado(a)',
      viuvo: 'Viúvo(a)',
      uniao_estavel: 'União Estável'
    };
    return estadoCivilMap[estadoCivil] || estadoCivil;
  };

  const getApartamentoLabel = (apartamentoId) => {
    const apartamento = apartamentos.find(a => a.apartamento_id === apartamentoId);
    return apartamento ? `${apartamento.apartamento_bloco} - ${apartamento.apartamento_numero}` : 'Sem apartamento';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Moradores</h1>
        </div>
        {(['sindico', 'subsindico', 'administrador', 'morador', 'usuario'].includes(user?.tipo) && (!isAdministrador() || getCondominioId())) ? (
          <button
            onClick={() => {
              setEditingMorador(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Novo Morador
          </button>
        ) : isAdministrador() && !getCondominioId() ? (
          <div className="text-sm text-gray-500">
            Selecione um condomínio para gerenciar moradores
          </div>
        ) : null}
      </div>

      {!getCondominioId() && isAdministrador() ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <MapPin className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Selecione um condomínio
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Para visualizar e gerenciar moradores, selecione um condomínio no menu superior.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Filtros */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={filterNomeInput}
                  onChange={(e) => setFilterNomeInput(e.target.value)}
                  placeholder="Buscar por nome"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bloco</label>
                <select
                  value={filters.bloco}
                  onChange={(e) => {
                    const bloco = e.target.value;
                    setFilters(prev => ({
                      ...prev,
                      bloco,
                      apartamento_id: bloco && prev.apartamento_id
                        ? (apartamentos.some(apt => String(apt.apartamento_id) === String(prev.apartamento_id) && String(apt.apartamento_bloco || '') === bloco) ? prev.apartamento_id : '')
                        : prev.apartamento_id
                    }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Todos</option>
                  {[...new Set(apartamentos.map(a => a.apartamento_bloco).filter(Boolean))].sort().map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apartamento</label>
                <select
                  value={filters.apartamento_id}
                  onChange={(e) => { setFilters(prev => ({ ...prev, apartamento_id: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Todos</option>
                  {apartamentos
                    .filter(apt => !filters.bloco || String(apt.apartamento_bloco || '') === filters.bloco)
                    .map(apt => (
                    <option key={apt.apartamento_id} value={apt.apartamento_id}>{apt.apartamento_bloco} - {apt.apartamento_numero}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => { setFilterNomeInput(''); setFilters({ nome: '', bloco: '', apartamento_id: '' }); setPagination(prev => ({ ...prev, page: 1 })); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Limpar filtros
                </button>
              </div>
            </div>
          </div>

          {/* Lista de Moradores */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="table-container">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Apartamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {moradores.map((morador) => (
                    <tr key={morador.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full btn btn-primary flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {morador.nome?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {morador.nome}
                            </div>
                            {/* Profissão oculta - para reativação futura */}
                            {/* {morador.profissao && (
                              <div className="text-sm text-gray-500">
                                {morador.profissao}
                              </div>
                            )} */}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {morador.cpf}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getApartamentoLabel(morador.apartamento_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {morador.telefone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {morador.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getTipoLabel(morador.tipo)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          morador.ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {morador.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              console.log('🖱️ Botão Editar clicado - Morador:', morador.morador_nome || morador.nome);
                              handleEdit(morador);
                            }}
                            className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {['sindico', 'administrador'].includes(user?.tipo) && (
                            <button
                              onClick={() => handleDelete(morador.morador_id || morador.id)}
                              className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginação */}
          {pagination.pages > 1 && (
            <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
              <div className="text-sm text-gray-700">
                Mostrando página {pagination.page} de {pagination.pages} · Total: {pagination.total} moradores
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingMorador ? 'Editar Morador' : 'Novo Morador'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingMorador(null);
                  resetForm();
                  setActiveTab('dados');
                  setFotoReconhecimento(null);
                }}
                className="modal-close-btn"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Abas */}
            <div className="tabs-container">
              <button
                type="button"
                onClick={() => setActiveTab('dados')}
                className={`tab-button ${activeTab === 'dados' ? 'tab-button-active' : ''}`}
              >
                Dados Pessoais
              </button>
              {/* Aba de reconhecimento facial - apenas para administradores */}
              {editingMorador && isAdministrador() && (
                <button
                  type="button"
                  onClick={() => setActiveTab('reconhecimento')}
                  className={`tab-button ${activeTab === 'reconhecimento' ? 'tab-button-active' : ''}`}
                >
                  <Camera className="h-4 w-4" />
                  Reconhecimento Facial
                </button>
              )}
            </div>
              
              {/* Exibir mensagem de erro */}
              {errorMessage && (
                <div className="alert alert-error">
                  <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="alert-content">
                    <h3 className="alert-title">Erro ao salvar morador</h3>
                    <p className="alert-message">{errorMessage}</p>
                  </div>
                </div>
              )}
              
              {/* Aba de Dados Pessoais */}
              {activeTab === 'dados' && (
                <form onSubmit={handleSubmit}>
                  <div className="form-section">
                    <h4 className="form-section-title">Dados Pessoais</h4>
                    <div className="form-grid form-grid-2">
                      <div className="form-field">
                        <label className="form-label form-label-required">Nome</label>
                        <input
                          type="text"
                          name="nome"
                          value={formData.nome}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                          placeholder="Digite o nome completo"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className={`form-label ${formData.sera_usuario ? 'form-label-required' : ''}`}>
                          CPF
                        </label>
                        <input
                          type="text"
                          name="cpf"
                          value={formData.cpf}
                          onChange={handleInputChange}
                          placeholder="000.000.000-00"
                          className="form-input"
                          required={formData.sera_usuario && !editingMorador?.usuario_id}
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">RG</label>
                        <input
                          type="text"
                          name="rg"
                          value={formData.rg}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Digite o RG"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Data de Nascimento</label>
                        <input
                          type="date"
                          name="data_nascimento"
                          value={formData.data_nascimento}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Sexo</label>
                        <select
                          name="sexo"
                          value={formData.sexo}
                          onChange={handleInputChange}
                          className="form-select"
                        >
                          <option value="nao_informado">Não informado</option>
                          <option value="masculino">Masculino</option>
                          <option value="feminino">Feminino</option>
                          <option value="outro">Outro</option>
                        </select>
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Estado Civil</label>
                        <select
                          name="estado_civil"
                          value={formData.estado_civil}
                          onChange={handleInputChange}
                          className="form-select"
                        >
                          <option value="">Selecione</option>
                          <option value="solteiro">Solteiro(a)</option>
                          <option value="casado">Casado(a)</option>
                          <option value="divorciado">Divorciado(a)</option>
                          <option value="viuvo">Viúvo(a)</option>
                          <option value="uniao_estavel">União Estável</option>
                        </select>
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label form-label-required">Telefone</label>
                        <input
                          type="text"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleInputChange}
                          maxLength="15"
                          placeholder="(11) 99999-9999"
                          className="form-input"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label form-label-required">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                          placeholder="email@exemplo.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dados Profissionais - OCULTO - Para reativação futura */}
                  {/* 
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Dados Profissionais</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profissão</label>
                    <input
                      type="text"
                      name="profissao"
                      value={formData.profissao}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Empresa</label>
                    <input
                      type="text"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Renda Mensal (R$)</label>
                    <input
                      type="number"
                      name="renda_mensal"
                      value={formData.renda_mensal}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  */}

                  <div className="form-section">
                    <h4 className="form-section-title">Tipo e Apartamento</h4>
                    <div className="form-grid form-grid-2">
                      <div className="form-field">
                        <label className="form-label form-label-required">Tipo de Morador</label>
                        <select
                          name="tipo"
                          value={formData.tipo}
                          onChange={handleInputChange}
                          required
                          className="form-select"
                        >
                          <option value="proprietario">Proprietário</option>
                          <option value="inquilino">Inquilino</option>
                          <option value="morador_temporario">Morador Temporário</option>
                        </select>
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Bloco</label>
                        {['administrador', 'sindico', 'subsindico', 'porteiro'].includes(user?.tipo) || isAdministrador() ? (
                          <select
                            value={blocoCadastro}
                            onChange={(e) => {
                              const bloco = e.target.value;
                              setBlocoCadastro(bloco);
                              setFormData(prev => {
                                const atual = apartamentos.find(apt => String(apt.apartamento_id) === String(prev.apartamento_id));
                                if (bloco && atual && String(atual.apartamento_bloco || '') !== bloco) {
                                  return { ...prev, apartamento_id: '' };
                                }
                                return prev;
                              });
                            }}
                            className="form-select"
                          >
                            <option value="">Todos</option>
                            {[...new Set(apartamentos.map(a => a.apartamento_bloco).filter(Boolean))].sort().map(b => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                        ) : (
                          <input type="text" value={blocoCadastro} readOnly className="form-input" />
                        )}
                      </div>

                      <div className="form-field">
                        <label className="form-label form-label-required">Apartamento</label>
                        {['administrador', 'sindico', 'subsindico', 'porteiro'].includes(user?.tipo) || isAdministrador() ? (
                          <select
                            name="apartamento_id"
                            value={formData.apartamento_id ? String(formData.apartamento_id) : ''}
                            onChange={handleInputChange}
                            required
                            className="form-select"
                          >
                            <option value="">Selecione o apartamento</option>
                            {apartamentos
                              .filter(apartamento => !blocoCadastro || String(apartamento.apartamento_bloco || '') === blocoCadastro)
                              .map(apartamento => (
                              <option key={apartamento.apartamento_id} value={apartamento.apartamento_id}>
                                {apartamento.apartamento_bloco} - {apartamento.apartamento_numero}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              type="hidden"
                              name="apartamento_id"
                              value={user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id || ''}
                            />
                            <input
                              type="text"
                              value={apartamentos.find(apt => 
                                apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                              ) 
                                ? `${apartamentos.find(apt => 
                                    apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                                  ).apartamento_bloco} - ${apartamentos.find(apt => 
                                    apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                                  ).apartamento_numero}`
                                : 'Carregando...'}
                              readOnly
                              className="form-input"
                              title="Como morador, você só pode cadastrar no seu apartamento"
                            />
                          </>
                        )}
                      </div>

                      {/* Checkbox para indicar se será usuário */}
                      <div className="form-field form-field-full">
                        <label className="form-label" style={{marginBottom: 0}}>
                          <input
                            type="checkbox"
                            name="sera_usuario"
                            checked={formData.sera_usuario}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          {' '}Será usuário do sistema
                        </label>
                        {formData.sera_usuario && !editingMorador?.usuario_id && formData.apartamento_id && !limiteUsuarios.podeAdicionar && (
                          <p className="form-error-message" style={{color: '#dc2626', marginTop: '8px'}}>
                            Já existem 2 usuários cadastrados nesta unidade. É permitido no máximo 2 usuários por unidade.
                          </p>
                        )}
                        {editingMorador?.usuario_id && (
                          <p className="form-error-message" style={{color: '#6b7280', marginTop: '8px'}}>
                            Este morador já possui usuário de acesso. A senha atual é mantida.
                          </p>
                        )}
                      </div>

                      {formData.sera_usuario && !editingMorador?.usuario_id && (
                        <div className="form-field form-field-full">
                          <label className="form-label form-label-required">Senha</label>
                          <input
                            type="password"
                            name="senha"
                            value={formData.senha}
                            onChange={handleInputChange}
                            required
                            className="form-input"
                            placeholder="Digite a senha (mínimo 6 caracteres)"
                            minLength={6}
                          />
                          <p className="form-error-message" style={{color: '#6b7280', marginTop: '4px'}}>
                            Senha mínima de 6 caracteres
                          </p>
                        </div>
                      )}
                  
                      {/* Ocultar campo "Vincular Usuário" quando sera_usuario estiver marcado */}
                      {isAdministrador() && !formData.sera_usuario && (
                        <div className="form-field">
                          <label className="form-label">Vincular Usuário</label>
                          {selectedUser ? (
                          <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <UserPlus size={16} className="text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{selectedUser?.usuario_nome || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{selectedUser?.usuario_email || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => setShowUserSelector(true)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Search size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={handleRemoveUser}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              placeholder="Nenhum usuário selecionado"
                              readOnly
                              className="flex-1 form-input"
                              style={{backgroundColor: '#f9fafb', color: '#6b7280'}}
                            />
                            <button
                              type="button"
                              onClick={() => setShowUserSelector(true)}
                              className="action-btn action-btn-edit"
                            >
                              <Search size={16} />
                            </button>
                          </div>
                        )}
                          <p className="form-error-message" style={{color: '#6b7280'}}>
                            Vincule um usuário existente para permitir login no sistema
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-field form-field-full">
                    <label className="form-label">Observações</label>
                    <textarea
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleInputChange}
                      rows="3"
                      className="form-textarea"
                      placeholder="Digite observações sobre o morador..."
                    />
                  </div>
                  
                  <div className="form-field form-field-full">
                    <label className="form-label" style={{marginBottom: 0}}>
                      <input
                        type="checkbox"
                        name="ativo"
                        checked={formData.ativo}
                        onChange={handleInputChange}
                        className="form-checkbox"
                      />
                      {' '}Morador Ativo
                    </label>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingMorador(null);
                        resetForm();
                        setActiveTab('dados');
                        setFotoReconhecimento(null);
                      }}
                      className="btn-base btn-secondary-custom"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-base btn-primary-custom btn-icon"
                    >
                      <Save className="h-4 w-4" />
                      {isSubmitting ? 'Salvando...' : (editingMorador ? 'Atualizar' : 'Criar')}
                    </button>
                  </div>
                </form>
              )}

              {/* Aba de Reconhecimento Facial - apenas para administradores */}
              {activeTab === 'reconhecimento' && editingMorador && isAdministrador() && (
                <div className="form-section">
                  <div className="alert alert-info">
                    <Camera className="alert-icon" />
                    <div className="alert-content">
                      <h4 className="alert-title">Reconhecimento Facial</h4>
                      <p className="alert-message">
                        Capture uma foto do morador para habilitar o reconhecimento facial na portaria.
                      </p>
                    </div>
                  </div>

                  {/* Indicador de salvamento */}
                  {salvandoFoto && (
                    <div className="alert alert-warning">
                      <LoadingSpinner size="sm" />
                      <span style={{marginLeft: '0.75rem'}}>Salvando foto...</span>
                    </div>
                  )}

                  {/* Área de preview/captura */}
                  <div className="flex flex-col items-center" style={{marginTop: '1.5rem'}}>
                    {fotoReconhecimento ? (
                      <>
                        <div style={{marginBottom: '1rem'}}>
                          <img
                            src={fotoReconhecimento}
                            alt="Foto de reconhecimento"
                            className="w-64 h-64 object-cover rounded-lg border-4 border-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowWebcam(true)}
                          className="btn-base btn-primary-custom btn-icon"
                        >
                          <Camera className="h-5 w-5" />
                          Capturar Nova Foto
                        </button>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center mb-4 border-2 border-dashed border-gray-300">
                          <div className="text-center">
                            <Camera className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">Nenhuma foto capturada</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowWebcam(true)}
                          className="btn-base btn-primary-custom btn-icon"
                        >
                          <Camera className="h-5 w-5" />
                          Capturar Foto
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Webcam Modal */}
      {showWebcam && (
        <WebcamCapture
          onCapture={handleCapturarFoto}
          onClose={() => setShowWebcam(false)}
        />
      )}

      {/* User Selector Modal */}
      <UserSelector
        isOpen={showUserSelector}
        onClose={() => setShowUserSelector(false)}
        onSelect={handleUserSelect}
        selectedUserId={selectedUser?.usuario_id || (selectedUser?.usuario && selectedUser.usuario.usuario_id)}
      />
    </div>
  );
};

export default Moradores;

// Estilos CSS personalizados para o modal de Moradores
const moradoresStyles = `
  .moradores-modal .input-field {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;
    background-color: #f9fafb;
  }
  
  .moradores-modal .input-field:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .moradores-modal .input-field:hover {
    border-color: #d1d5db;
    background-color: #ffffff;
  }
  
  .moradores-modal .btn-primary {
    background-color: #3b82f6;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .moradores-modal .btn-primary:hover:not(:disabled) {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  .moradores-modal .btn-primary:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
  
  .moradores-modal .btn-secondary {
    background-color: #6b7280;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .moradores-modal .btn-secondary:hover {
    background-color: #4b5563;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
  }
`;

// Adicionar estilos ao documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = moradoresStyles;
  document.head.appendChild(styleSheet);
}
